import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  BookOpen, Upload, Loader2, Trash2, FileText, ListChecks, Layers,
  GraduationCap, Image as ImageIcon, Wand2, CheckCircle2, AlertTriangle,
  ExternalLink, RefreshCw, Eye, RotateCcw, Cpu, ChevronDown, Save, Download,
} from 'lucide-react';
import { oustazApi, fileUrl } from '../../api/oustaz';
import { EmptyState, InlineSpinner } from './ui';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';

const Panel = ({ className = '', children, ...rest }) => (
  <div className={`bg-card border border-border rounded-md shadow-sm ${className}`} {...rest}>{children}</div>
);

const RESOURCE_TYPES = [
  { id: 'lesson', label: 'Lesson', icon: BookOpen, color: '#3B6FB6' },
  { id: 'flashcards', label: 'Flashcards', icon: Layers, color: '#3E9C7B' },
  { id: 'quiz', label: 'Quiz', icon: ListChecks, color: '#E0A53B' },
  { id: 'infographic_brief', label: 'Infographic brief', icon: ImageIcon, color: '#D9482B' },
];

const GEN_STEP_LABELS = {
  queued: 'Queued…', reading_chunks: 'Reading book content…',
  generating: 'Generating resource with AI…', generating_images: 'Generating illustrations…',
  saving_html: 'Saving draft…', completed: 'Completed', failed: 'Failed',
};

const STATUS_META = {
  queued: { label: 'Queued', cls: 'bg-muted text-muted-foreground' },
  processing: { label: 'Processing', cls: 'bg-amber-100 text-amber-800 border border-amber-200' },
  completed: { label: 'Ready', cls: 'bg-green-100 text-green-800 border border-green-200' },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-800 border border-red-200' },
};

export default function OustazBooks({ orgId, config, onOpenLibrary }) {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);

  // upload
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // selection + generation
  const [selectedBook, setSelectedBook] = useState(null);
  const [resourceType, setResourceType] = useState('lesson');
  const [fromPage, setFromPage] = useState('1');
  const [toPage, setToPage] = useState('1');
  const [generating, setGenerating] = useState(false);
  const [job, setJob] = useState(null);
  const [result, setResult] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reparsingId, setReparsingId] = useState(null);

  // preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewChunks, setPreviewChunks] = useState([]);
  const [previewRange, setPreviewRange] = useState('');
  const [downloadingJson, setDownloadingJson] = useState(false);

  const pollRef = useRef(null);
  const genPollRef = useRef(null);

  // admin-only model settings
  const isAdmin = !!config?.is_admin;
  const [modelsOpen, setModelsOpen] = useState(false);
  const [modelsCfg, setModelsCfg] = useState(null);
  const [modelForm, setModelForm] = useState({ lesson_model: '', lesson_fallback_model: '', image_model: '' });
  const [savingModels, setSavingModels] = useState(false);

  const hasKey = !!config?.has_openrouter_key;
  const lessonKeysMissing = resourceType === 'lesson' && (!config?.has_claude_key || !config?.has_openai_key);
  const missingKeyLabel = !config?.has_claude_key && !config?.has_openai_key
    ? 'Anthropic and OpenAI API keys'
    : (!config?.has_claude_key ? 'Anthropic API key' : 'OpenAI API key');

  const loadBooks = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try { setBooks(await oustazApi.listBooks(orgId)); }
    catch { toast.error('Failed to load books'); }
    finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { loadBooks(); }, [loadBooks]);

  // Load admin model settings
  useEffect(() => {
    if (!isAdmin || !orgId) return;
    let cancelled = false;
    (async () => {
      try {
        const cfg = await oustazApi.getModels(orgId);
        if (cancelled) return;
        setModelsCfg(cfg);
        setModelForm({
          lesson_model: cfg.selected.lesson_model,
          lesson_fallback_model: cfg.selected.lesson_fallback_model,
          image_model: cfg.selected.image_model,
        });
      } catch { /* non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, [isAdmin, orgId]);

  const saveModels = async () => {
    setSavingModels(true);
    try {
      const r = await oustazApi.saveModels({ organization_id: orgId, ...modelForm });
      setModelsCfg((prev) => prev ? { ...prev, selected: r.selected } : prev);
      toast.success('AI models updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save models');
    } finally { setSavingModels(false); }
  };

  // Poll while any book is still processing to update "Page X of Y".
  useEffect(() => {
    const anyProcessing = books.some((b) => b.status === 'queued' || b.status === 'processing');
    if (anyProcessing && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        try { setBooks(await oustazApi.listBooks(orgId)); } catch { /* ignore */ }
      }, 2500);
    } else if (!anyProcessing && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
    return () => { if (!anyProcessing && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [books, orgId]);

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (genPollRef.current) clearInterval(genPollRef.current);
  }, []);

  // keep selectedBook in sync with refreshed list
  useEffect(() => {
    if (!selectedBook) return;
    const fresh = books.find((b) => b.id === selectedBook.id);
    if (fresh && fresh !== selectedBook) setSelectedBook(fresh);
  }, [books]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async () => {
    if (!uploadFile) { toast.error('Choose a PDF book to upload'); return; }
    if (!uploadFile.name.toLowerCase().endsWith('.pdf')) { toast.error('Only PDF files are accepted'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      await oustazApi.uploadBook(orgId, fd, uploadTitle.trim() || uploadFile.name);
      toast.success('Book uploaded — parsing started');
      setUploadTitle(''); setUploadFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await loadBooks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const selectBook = (b) => {
    setSelectedBook(b);
    setResult(null); setJob(null);
    setFromPage('1');
    setToPage(String(b.page_count || 1));
  };

  const startGenerate = async () => {
    if (!selectedBook) return;
    const from = Math.max(1, parseInt(fromPage, 10) || 1);
    const to = Math.max(from, parseInt(toPage, 10) || from);
    if (to > (selectedBook.page_count || to)) { toast.error(`This book has ${selectedBook.page_count} pages`); return; }
    setGenerating(true); setResult(null); setJob(null);
    try {
      const { job_id } = await oustazApi.generateBookResource(selectedBook.id, {
        organization_id: orgId, resource_type: resourceType, from_page: from, to_page: to,
      });
      pollGenJob(job_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start generation');
      setGenerating(false);
    }
  };

  const pollGenJob = (jobId) => {
    if (genPollRef.current) clearInterval(genPollRef.current);
    genPollRef.current = setInterval(async () => {
      try {
        const j = await oustazApi.getJob(jobId);
        setJob(j);
        if (j.status === 'completed') {
          clearInterval(genPollRef.current); genPollRef.current = null;
          setGenerating(false); setResult(j.result);
          toast.success('Draft resource created in your Library');
        } else if (j.status === 'failed') {
          clearInterval(genPollRef.current); genPollRef.current = null;
          setGenerating(false);
          toast.error(j.error || 'Generation failed');
        }
      } catch { /* ignore transient */ }
    }, 2000);
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await oustazApi.deleteBook(deleteTarget.id);
      toast.success('Book deleted');
      if (selectedBook?.id === deleteTarget.id) setSelectedBook(null);
      setDeleteTarget(null);
      await loadBooks();
    } catch { toast.error('Failed to delete book'); }
  };

  const handleReparse = async (book) => {
    setReparsingId(book.id);
    try {
      const r = await oustazApi.reparseBook(book.id, { organization_id: orgId });
      toast.success(`Re-parsing ${r.pages?.length || 0} page(s)…`);
      await loadBooks();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to start re-parsing');
    } finally { setReparsingId(null); }
  };

  const openPreview = async () => {
    if (!selectedBook) return;
    const from = Math.max(1, parseInt(fromPage, 10) || 1);
    const to = Math.max(from, parseInt(toPage, 10) || from);
    setPreviewRange(`Pages ${from}–${to}`);
    setPreviewChunks([]);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const chunks = await oustazApi.getBookChunks(selectedBook.id, from, to);
      setPreviewChunks(chunks || []);
    } catch {
      toast.error('Failed to load preview');
    } finally { setPreviewLoading(false); }
  };

  const downloadChunksJson = async (format) => {
    if (!selectedBook || downloadingJson) return;
    setDownloadingJson(true);
    try {
      // Always export ALL pages (no range filter).
      const chunks = (await oustazApi.getBookChunks(selectedBook.id)) || [];
      if (!chunks.length) {
        toast.error('No parsed chunks to download yet');
        return;
      }

      const safeName = (selectedBook.title || selectedBook.name || 'book')
        .replace(/[^\w\-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'book';

      let payload;
      let suffix;
      if (format === 'wrapped') {
        payload = {
          book: {
            id: selectedBook.id,
            title: selectedBook.title || selectedBook.name || '',
            page_count: selectedBook.page_count ?? null,
            chunk_count: chunks.length,
            exported_at: new Date().toISOString(),
          },
          chunks,
        };
        suffix = 'book';
      } else {
        payload = chunks;
        suffix = 'chunks';
      }

      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}_${suffix}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${chunks.length} chunk(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to download JSON');
    } finally {
      setDownloadingJson(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="oustaz-books-panel">
      {!hasKey && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="oustaz-books-key-banner">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">OpenRouter API key not set. Book parsing & resource generation need it. Configure it in Settings → Integrations.</span>
        </div>
      )}

      {/* Admin: AI model selection */}
      {isAdmin && modelsCfg && (
        <Panel className="overflow-hidden" data-testid="oustaz-models-card">
          <button
            type="button"
            onClick={() => setModelsOpen((v) => !v)}
            className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
            data-testid="oustaz-models-toggle"
          >
            <Cpu className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold flex-1">AI Models (admin)</span>
            <span className="hidden sm:flex items-center gap-1.5 text-xs text-muted-foreground">
              <Badge variant="secondary" className="text-[10px]">{modelsCfg.selected.lesson_model}</Badge>
              <Badge variant="secondary" className="text-[10px]">{modelsCfg.selected.image_model}</Badge>
            </span>
            <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${modelsOpen ? 'rotate-180' : ''}`} />
          </button>
          {modelsOpen && (
            <div className="px-4 pb-4 pt-1 border-t space-y-4">
              <p className="text-xs text-muted-foreground">Choose which OpenRouter models power lesson generation and illustrations. Applies to all teachers in this school.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1.5 block">Lesson model</label>
                  <Select value={modelForm.lesson_model} onValueChange={(v) => setModelForm((f) => ({ ...f, lesson_model: v }))}>
                    <SelectTrigger data-testid="oustaz-model-lesson"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {modelsCfg.options.text.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium mb-1.5 block">Fallback model (if truncated)</label>
                  <Select value={modelForm.lesson_fallback_model} onValueChange={(v) => setModelForm((f) => ({ ...f, lesson_fallback_model: v }))}>
                    <SelectTrigger data-testid="oustaz-model-fallback"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {modelsCfg.options.text.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium mb-1.5 block">Image model</label>
                  <Select value={modelForm.image_model} onValueChange={(v) => setModelForm((f) => ({ ...f, image_model: v }))}>
                    <SelectTrigger data-testid="oustaz-model-image"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {modelsCfg.options.image.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button size="sm" onClick={saveModels} disabled={savingModels} className="gap-2" data-testid="oustaz-models-save">
                  {savingModels ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save models
                </Button>
                <span className="text-xs text-muted-foreground">Defaults: {modelsCfg.defaults.lesson_model} · {modelsCfg.defaults.image_model}</span>
              </div>
            </div>
          )}
        </Panel>
      )}

      {/* Upload */}
      <Panel className="p-4">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Upload className="w-4 h-4" /> Upload a scanned book (PDF)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
          <div className="space-y-2">
            <Input
              placeholder="Book title (optional)"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              data-testid="oustaz-book-title-input"
            />
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-muted-foreground file:mr-3 file:py-2 file:px-3 file:rounded-md file:border file:border-border file:bg-muted file:text-sm file:font-medium hover:file:bg-muted/70"
              data-testid="oustaz-book-file-input"
            />
          </div>
          <Button onClick={handleUpload} disabled={uploading || !hasKey || !uploadFile} className="gap-2" data-testid="oustaz-book-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload & Parse
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Each page is rendered at 2x and parsed with OpenRouter vision (openrouter/auto). Large books can take several minutes.</p>
      </Panel>

      {/* Books list */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">Your books</h3>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={loadBooks} data-testid="oustaz-books-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
        </div>
        {loading ? (
          <InlineSpinner />
        ) : books.length === 0 ? (
          <EmptyState icon={BookOpen} title="No books yet" description="Upload a scanned PDF book to get started." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="oustaz-books-grid">
            {books.map((b) => {
              const sm = STATUS_META[b.status] || STATUS_META.queued;
              const pct = b.page_count ? Math.round(((b.processed_pages || 0) / b.page_count) * 100) : 0;
              const active = selectedBook?.id === b.id;
              return (
                <Panel key={b.id} className={`p-4 ${active ? 'ring-2 ring-primary/40 border-primary' : ''}`} data-testid={`oustaz-book-${b.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate" title={b.title}>{b.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{b.filename}</p>
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${sm.cls}`} data-testid={`oustaz-book-status-${b.id}`}>{sm.label}</span>
                  </div>

                  {(b.status === 'processing' || b.status === 'queued') && (
                    <div className="mt-3">
                      <Progress value={pct} className="h-2" />
                      <p className="text-xs text-muted-foreground mt-1" data-testid={`oustaz-book-progress-${b.id}`}>
                        Page {b.processed_pages || 0} of {b.page_count || '?'}
                      </p>
                    </div>
                  )}
                  {b.status === 'completed' && (
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                      {b.page_count} pages · {b.chunk_count || 0} chunks
                    </p>
                  )}
                  {b.status === 'failed' && (
                    <p className="text-xs text-red-600 mt-2" title={b.error}>{b.error || 'Parsing failed'}</p>
                  )}
                  {(b.failed_pages?.length > 0) && b.status !== 'processing' && b.status !== 'queued' && (
                    <div className="mt-2 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5" data-testid={`oustaz-book-failed-${b.id}`}>
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                      <span className="text-[11px] text-amber-800 flex-1 truncate" title={`Failed pages: ${b.failed_pages.join(', ')}`}>
                        {b.failed_pages.length} page{b.failed_pages.length > 1 ? 's' : ''} failed (p{b.failed_pages.slice(0, 6).join(', p')}{b.failed_pages.length > 6 ? '…' : ''})
                      </span>
                      <Button size="sm" variant="outline" className="h-6 px-2 gap-1 text-[11px]" disabled={reparsingId === b.id || !hasKey}
                        onClick={() => handleReparse(b)} data-testid={`oustaz-book-retry-${b.id}`}>
                        {reparsingId === b.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                        Retry
                      </Button>
                    </div>
                  )}

                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm" variant={active ? 'default' : 'outline'} className="gap-1.5 flex-1"
                      disabled={b.status !== 'completed'}
                      onClick={() => selectBook(b)}
                      data-testid={`oustaz-book-select-${b.id}`}
                    >
                      <Wand2 className="w-3.5 h-3.5" /> Create resource
                    </Button>
                    <Button size="sm" variant="outline" className="text-destructive hover:text-destructive" onClick={() => setDeleteTarget(b)} data-testid={`oustaz-book-delete-${b.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </Panel>
              );
            })}
          </div>
        )}
      </div>

      {/* Generate panel */}
      {selectedBook && selectedBook.status === 'completed' && (
        <Panel className="p-4 space-y-4" data-testid="oustaz-book-generate-panel">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold">Create a resource from “{selectedBook.title}”</h3>
          </div>

          <div>
            <label className="text-xs font-medium mb-2 block">Resource type</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {RESOURCE_TYPES.map((rt) => {
                const Icon = rt.icon;
                const on = resourceType === rt.id;
                return (
                  <button
                    key={rt.id} type="button" onClick={() => setResourceType(rt.id)}
                    className={`flex items-center gap-2 rounded-lg border p-2.5 text-left text-sm transition-colors ${on ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'border-border hover:border-primary/40'}`}
                    data-testid={`oustaz-book-restype-${rt.id}`}
                  >
                    <span className="rounded-md p-1.5 text-white flex-shrink-0" style={{ backgroundColor: rt.color }}><Icon className="w-4 h-4" /></span>
                    <span className="font-medium">{rt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs font-medium mb-1 block">From page</label>
              <Input type="number" min={1} max={selectedBook.page_count} value={fromPage}
                onChange={(e) => setFromPage(e.target.value)} className="w-28" data-testid="oustaz-book-from-page" />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">To page</label>
              <Input type="number" min={1} max={selectedBook.page_count} value={toPage}
                onChange={(e) => setToPage(e.target.value)} className="w-28" data-testid="oustaz-book-to-page" />
            </div>
            <span className="text-xs text-muted-foreground pb-2">of {selectedBook.page_count} pages</span>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" onClick={openPreview} className="gap-2" data-testid="oustaz-book-preview-btn">
                <Eye className="w-4 h-4" /> Preview content
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" disabled={downloadingJson || !(selectedBook.chunk_count > 0)}
                    className="gap-2" data-testid="oustaz-book-download-json-btn">
                    {downloadingJson ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Download JSON
                    <ChevronDown className="w-3.5 h-3.5 opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64">
                  <DropdownMenuLabel>Export all pages as JSON</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => downloadChunksJson('raw')} data-testid="oustaz-book-download-json-raw">
                    <FileText className="w-4 h-4 mr-2" />
                    <div className="flex flex-col">
                      <span className="text-sm">Raw chunks</span>
                      <span className="text-xs text-muted-foreground">Array of parsed chunks</span>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => downloadChunksJson('wrapped')} data-testid="oustaz-book-download-json-wrapped">
                    <BookOpen className="w-4 h-4 mr-2" />
                    <div className="flex flex-col">
                      <span className="text-sm">Book + chunks</span>
                      <span className="text-xs text-muted-foreground">With metadata, reusable in Upload JSON</span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button onClick={startGenerate} disabled={generating || !hasKey || lessonKeysMissing} className="gap-2" data-testid="oustaz-book-generate-btn">
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <GraduationCap className="w-4 h-4" />}
                Generate draft
              </Button>
            </div>
          </div>

          {lessonKeysMissing && (
            <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="oustaz-lesson-keys-warning">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              <span>Lesson generation needs the {missingKeyLabel} saved in Settings → Integrations. Ask a super admin to add {(!config?.has_claude_key && !config?.has_openai_key) ? 'them' : 'it'}.</span>
            </div>
          )}

          {job && job.status !== 'completed' && (
            <div className="rounded-lg border border-border p-3" data-testid="oustaz-book-job-progress">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="flex items-center gap-2 font-medium"><Loader2 className="w-4 h-4 animate-spin" />{job.message || GEN_STEP_LABELS[job.step] || job.step}</span>
                <span className="text-muted-foreground">{job.progress || 0}%</span>
              </div>
              <Progress value={job.progress || 0} className="h-2" />
            </div>
          )}

          {result && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4" data-testid="oustaz-book-result">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-800">Draft created: {result.title}</span>
                <Badge variant="outline" className="ml-1 text-[10px]">Draft — AI Generated</Badge>
              </div>
              <div className="flex flex-wrap gap-2 mt-1">
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => window.open(fileUrl(result.html_url), '_blank', 'noopener')} data-testid="oustaz-book-result-open">
                  <ExternalLink className="w-3.5 h-3.5" /> Open draft
                </Button>
                <Button size="sm" className="gap-1.5" onClick={() => onOpenLibrary && onOpenLibrary()} data-testid="oustaz-book-result-library">
                  <Layers className="w-3.5 h-3.5" /> Review in My Lessons
                </Button>
              </div>
            </div>
          )}
        </Panel>
      )}

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl" data-testid="oustaz-book-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Eye className="w-4 h-4" /> Content preview</DialogTitle>
            <DialogDescription>{previewRange} · {previewChunks.length} chunk{previewChunks.length === 1 ? '' : 's'} — review before generating</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
            {previewLoading ? (
              <div className="py-8"><InlineSpinner /></div>
            ) : previewChunks.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No extracted content found for this page range.</p>
            ) : (
              previewChunks.map((c) => (
                <div key={c.id} className="rounded-md border border-border p-2.5" data-testid={`oustaz-preview-chunk-${c.chunk_id}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant="outline" className="text-[10px]">p{c.page_number}</Badge>
                    <Badge variant="secondary" className="text-[10px] capitalize">{c.type?.replace('_', ' ')}</Badge>
                    {c.language && <Badge variant="secondary" className="text-[10px] uppercase">{c.language}</Badge>}
                    {c.image_regeneration_prompt && <Badge variant="outline" className="text-[10px] gap-1"><ImageIcon className="w-3 h-3" /> illustration</Badge>}
                  </div>
                  <p className="text-sm whitespace-pre-wrap break-words">{c.text || <span className="italic text-muted-foreground">(no text)</span>}</p>
                  {c.image_regeneration_prompt && (
                    <p className="text-[11px] text-muted-foreground mt-1 italic">Image prompt: {c.image_regeneration_prompt}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete book?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes “{deleteTarget?.title}” and all its parsed chunks. Generated resources already in your Library are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="oustaz-book-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
